import { useCallback, useEffect, useRef, useState } from 'react';
import { Schedule } from '@/features/accounts/types';
import { useActivateSchedule } from './useActivateSchedule';
import { useReactivateSchedule } from '../../management/hooks/useReactivateSchedule';
import { useUpdateSchedulePreferences } from '../../configuration/hooks/useUpdateSchedulePreferences';
import { type SchedulePreferencesPayload } from '@/features/accounts/api';
import { trackEvent } from '@/shared/analytics';

export type ActivationIntent = 'activate' | 'reactivate';

type ActivationFlowState =
  | {
      schedule: Schedule;
      step: 'configure';
      intent: ActivationIntent;
      preferences?: SchedulePreferencesPayload;
    }
  | {
      schedule: Schedule;
      step: 'payment';
      intent: 'activate';
      preferences: SchedulePreferencesPayload;
    }
  | { schedule: Schedule; step: 'payment'; intent: 'reactivate'; preferences: SchedulePreferencesPayload };

interface UseScheduleActivationOptions {
  initialSchedule?: Schedule;
  onActivated?: () => void;
  onCancelled?: () => void;
}

export interface UseScheduleActivationReturn {
  activationFlow: ActivationFlowState | null;
  startActivation: (schedule: Schedule, intent?: ActivationIntent) => void;
  submitConfiguration: (preferences: SchedulePreferencesPayload) => Promise<void>;
  backToConfigure: () => void;
  cancelActivation: () => void;
  confirmActivation: (scheduleId: number) => Promise<unknown>;
  completeActivation: () => void;
}

function configureStep(
  schedule: Schedule,
  intent: ActivationIntent,
  preferences?: SchedulePreferencesPayload
): ActivationFlowState {
  return preferences
    ? { schedule, step: 'configure', intent, preferences }
    : { schedule, step: 'configure', intent };
}

function collectedPreferences(
  flow: ActivationFlowState
): SchedulePreferencesPayload | undefined {
  return 'preferences' in flow ? flow.preferences : undefined;
}

function reportActivationStarted(schedule: Schedule): void {
  trackEvent('activation_started', {
    schedule_id: schedule.id,
    funded: schedule.funded,
  });
}

function reportActivationCancelled(schedule: Schedule): void {
  trackEvent('activation_cancelled', {
    schedule_id: schedule.id,
    funded: schedule.funded,
  });
}

export function useScheduleActivation({
  initialSchedule,
  onActivated,
  onCancelled,
}: UseScheduleActivationOptions): UseScheduleActivationReturn {
  const [activationFlow, setActivationFlow] = useState<ActivationFlowState | null>(() =>
    initialSchedule ? configureStep(initialSchedule, 'activate') : null
  );
  const reportedInitialSchedule = useRef(false);
  const settledRef = useRef(false);

  const activateSchedule = useActivateSchedule();
  const reactivateSchedule = useReactivateSchedule();
  const updatePreferences = useUpdateSchedulePreferences();

  useEffect(() => {
    if (!initialSchedule || reportedInitialSchedule.current) return;
    reportedInitialSchedule.current = true;
    reportActivationStarted(initialSchedule);
  }, [initialSchedule]);

  const startActivation = useCallback((schedule: Schedule, intent: ActivationIntent = 'activate') => {
    settledRef.current = false;
    reportActivationStarted(schedule);
    setActivationFlow(configureStep(schedule, intent));
  }, []);

  const submitConfiguration = useCallback(
    async (preferences: SchedulePreferencesPayload) => {
      if (!activationFlow) return;

      const { schedule, intent } = activationFlow;
      if (intent === 'reactivate') {
        setActivationFlow({ schedule, step: 'payment', intent, preferences });
        return;
      }

      const updated = await updatePreferences.mutateAsync({ scheduleId: schedule.id, preferences });
      setActivationFlow({ schedule: updated ?? schedule, step: 'payment', intent, preferences });
    },
    [activationFlow, updatePreferences]
  );

  const backToConfigure = useCallback(() => {
    setActivationFlow((prev) =>
      prev ? configureStep(prev.schedule, prev.intent, collectedPreferences(prev)) : null
    );
  }, []);

  const cancelActivation = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;

    if (activationFlow) {
      reportActivationCancelled(activationFlow.schedule);
    }
    setActivationFlow(null);
    onCancelled?.();
  }, [activationFlow, onCancelled]);

  const confirmActivation = useCallback(
    (scheduleId: number) => {
      if (activationFlow?.step === 'payment' && activationFlow.intent === 'reactivate') {
        return reactivateSchedule.mutateAsync({ scheduleId, preferences: activationFlow.preferences });
      }
      return activateSchedule.mutateAsync(scheduleId);
    },
    [activationFlow, reactivateSchedule, activateSchedule]
  );

  const completeActivation = useCallback(() => {
    settledRef.current = true;
    setActivationFlow(null);
    onActivated?.();
  }, [onActivated]);

  return {
    activationFlow,
    startActivation,
    submitConfiguration,
    backToConfigure,
    cancelActivation,
    confirmActivation,
    completeActivation,
  };
}
